import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { gql } from '@apollo/client/core';

import { InMemoryTotalFragment } from '../../../types/__generated__';
import usePageQuery, { buildQuery } from '../../hooks/usePageQuery';
import parseCurrency from '../../utils/parseCurrency';

import styles from './InMemoryTotal.scss';
import InMemoryTotalSkeleton from './InMemoryTotalSkeleton';
import useBasePageQuery from '../../hooks/useBasePageQuery';

const FRAGMENT = buildQuery(gql`
  fragment InMemoryTotal on Page {
    inMemory {
      firstNameOfDeceased
      lastNameOfDeceased
      dateOfBirth
      dateOfDeath
    }
  }
`);

export const InMemoryTotal: FunctionComponent = () => {
  const { loading, error, refetch, data } = usePageQuery<InMemoryTotalFragment>(
    FRAGMENT,
  );
  const { data: basePageData } = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });

  if (loading)
    return <InMemoryTotalSkeleton animated displayRefreshButton={false} />;

  if (error) {
    return <InMemoryTotalSkeleton onRefresh={refetch} />;
  }

  if (!data || !data.inMemory || !basePageData?.page) {
    return null;
  }

  const {
    inMemory: {
      firstNameOfDeceased,
      lastNameOfDeceased,
      dateOfBirth,
      dateOfDeath,
    },
  } = data;
  const { donationSummary } = basePageData.page;

  const donationCurrency = donationSummary?.totalAmount?.currencyCode ?? 'GBP';
  const donationValue = donationSummary?.totalAmount?.value
    ? donationSummary.totalAmount.value / 100
    : 0;

  const dateToString = (date: string | number): string => {
    return new Date(date).getTime().toString();
  };

  return (
    <>
      <h1 data-qa="in-memory-heading-name" className={styles.name}>
        <span className="jg-sr-only">
          {t('inMemory.total.fundraisingInMemoryOf')}{' '}
        </span>
        {firstNameOfDeceased} {lastNameOfDeceased}
      </h1>
      {dateOfBirth && dateOfDeath && (
        <p
          data-qa="in-memory-life-dates"
          className="jg-text--large jg-color--nobel jg-space-mbn"
        >
          {t('inMemory.total.lifeDates', {
            dateOfBirth: dateToString(dateOfBirth),
            dateOfDeath: dateToString(dateOfDeath),
          })}
        </p>
      )}
      <p className="jg-text--large jg-color--nobel jg-space-mtmd jg-space-mbn">
        <span className={styles.amount}>
          {parseCurrency(donationCurrency, donationValue, {
            roundDown: true,
            wholeNumber: true,
          })}
        </span>{' '}
        {t('inMemory.total.raisedInMemory')}
      </p>
    </>
  );
};
